// apps/client/src/pages/employees/Employees.tsx
import {
  Employee,
  EmployeeAllocationDeleteResponse,
  EmployeeAllocationUpdateRequestBody,
  EmployeeAllocationUpdateResponse,
  EmployeesFetchResponse,
  EmployeeType,
  ProjectEmployeesFetchResponse,
  ResearchActivity,
  ResearchActivityType,
  UserRole,
  XeroSyncEmployeesResponse,
} from '@avanzu/types';
import calcProjectStartAndEndDates from '@avanzu/utils/dist/dates';
import {
  CheckCircle,
  PeopleOutlined,
  PersonAddAlt1,
  PersonRemoveAlt1Outlined,
  ScienceOutlined,
} from '@mui/icons-material';
import {
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { EmptyStateContent } from '../../components/EmptyStateContent';
import HelpComponent from '../../components/HelpComponent';
import { LoadingComponent } from '../../components/LoadingComponent';
import PaperSection from '../../components/PaperSection';
import { useAuth } from '../../contexts/AuthContext';
import { useProject } from '../../contexts/ProjectContext';
import ContentWrapper from '../../layout/ContentWrapper';
import { apiClient } from '../../services';
import { fetchProjectActivities } from '../../services/projects';
import { EmployeeAllocationRow } from './components/EmployeeAllocationRow';
import PersonTickIcon from './components/PersonTickIcon';
import XeroSyncEmployees from './components/XeroSyncEmployees';
import { useNavigate } from 'react-router-dom';

export const EmployeePageRoles = [UserRole.AVANZU, UserRole.OWNER, UserRole.ADMIN, UserRole.MEMBER];
interface EmployeeAllocation {
  employeeId: string;
  projectAllocation: number;
  roleDescription: string;
  startDate: Date;
  endDate?: Date;
  activityAllocations: Record<string, number>;
  hasExistingAllocation: boolean;
}

export const Employees: React.FC = () => {
  const [employees, setEmployees] = useState<Employee[]>([]);
  const [activities, setActivities] = useState<ResearchActivity[]>([]);
  const [allocations, setAllocations] = useState<Record<string, EmployeeAllocation>>({});
  const [loading, setLoading] = useState(true);
  const [activeOnly, setActiveOnly] = useState(true);
  const [typeFilter, setTypeFilter] = useState<EmployeeType | ''>('');
  const [saving, setSaving] = useState<string | null>(null);
  const [syncResult, setSyncResult] = useState<XeroSyncEmployeesResponse | null>(null);

  const navigate = useNavigate();

  const { organisationId, currentOrganisation } = useAuth();
  const { currentProject } = useProject();
  const { enqueueSnackbar } = useSnackbar();

  const projectId = currentProject?.id;

  // Compute the financial-year window for the current project so we can ask
  // the server to filter to employees who were employed at any point during
  // it — the AusIndustry-correct definition of "active for this FY".
  const financialYearRange = React.useMemo(() => {
    if (!currentProject?.taxYear || !currentOrganisation?.taxYearStartMonth) return null;
    const startYear = currentProject.taxYear.slice(0, 4);
    const { startDate, endDate } = calcProjectStartAndEndDates(
      startYear,
      String(currentOrganisation.taxYearStartMonth),
    );
    return {
      from: startDate.toISOString().slice(0, 10),
      to: endDate.toISOString().slice(0, 10),
    };
  }, [currentProject?.taxYear, currentOrganisation?.taxYearStartMonth]);

  useEffect(() => {
    if (organisationId && projectId) {
      loadData();
    }
    // financialYearRange is derived from project.taxYear + org.taxYearStartMonth
    // — both already in the deps via projectId / org, but listing it here
    // keeps the intent obvious when activeOnly is on.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organisationId, projectId, activeOnly, typeFilter, financialYearRange?.from, financialYearRange?.to]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Fetch employees and activities in parallel
      const searchParams = new URLSearchParams();
      if (activeOnly) {
        searchParams.set('activeOnly', 'true');
        // When we know the financial year, ask the server to apply the
        // "employed during this FY" overlap filter instead of the boolean
        // isActive flag — covers people terminated mid-year and people who
        // started after we last imported their isActive state.
        if (financialYearRange) {
          searchParams.set('fyFrom', financialYearRange.from);
          searchParams.set('fyTo', financialYearRange.to);
        }
      }
      if (typeFilter) searchParams.set('type', typeFilter);

      const queryString = searchParams.toString();
      const employeesUrl = `/organisations/${organisationId}/employees${queryString ? `?${queryString}` : ''}`;

      const [employeesResponse, activitiesData] = await Promise.all([
        apiClient.get(employeesUrl),
        fetchProjectActivities(organisationId!, projectId!),
      ]);

      const employeesData: EmployeesFetchResponse = await employeesResponse.json();

      if (employeesData.success) {
        setEmployees(employeesData.employees);
        setActivities(activitiesData.activities);

        // Initialize allocations for all employees
        const initialAllocations: Record<string, EmployeeAllocation> = {};

        employeesData.employees.forEach((employee) => {
          initialAllocations[employee.id] = {
            employeeId: employee.id,
            projectAllocation: 0,
            roleDescription: '',
            startDate: new Date(),
            endDate: undefined,
            activityAllocations: {},
            hasExistingAllocation: false,
          };
        });
        setAllocations(initialAllocations);

        // Load existing allocations from project employees endpoint
        await loadExistingAllocations(organisationId!, projectId!);
      } else {
        enqueueSnackbar(employeesData.message || 'Failed to fetch employees', { variant: 'error' });
      }
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : 'Failed to load data', {
        variant: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSyncComplete = (result: XeroSyncEmployeesResponse) => {
    setSyncResult(result);
    // Reload data after successful sync
    if (result.success) {
      enqueueSnackbar(`Successfully synced ${result.processed} employees`, {
        variant: 'success',
      });
      loadData();
    }
  };

  const loadExistingAllocations = async (organisationId: string, projectId: string) => {
    try {
      const response = await apiClient.get(
        `/organisations/${organisationId}/projects/${projectId}/employees`
      );

      if (response.ok) {
        const data: ProjectEmployeesFetchResponse = await response.json();

        if (data.success && data.employees && Array.isArray(data.employees)) {
          // Update allocations with existing data
          setAllocations((prev) => {
            const updated = { ...prev };

            data.employees.forEach((employeeWithAllocations) => {
              // Check if the employee exists in our current allocations and has the expected structure
              if (
                updated[employeeWithAllocations.id] &&
                employeeWithAllocations.projectAllocations &&
                Array.isArray(employeeWithAllocations.projectAllocations)
              ) {
                // Find the project allocation for this specific project
                const projectAllocation = employeeWithAllocations.projectAllocations.find(
                  (pa) => pa.projectId === projectId
                );

                if (projectAllocation) {
                  // Convert activity allocations array to Record format
                  const activityAllocationsRecord: Record<string, number> = {};

                  // Check if activityAllocations exists and is an array
                  if (
                    employeeWithAllocations.activityAllocations &&
                    Array.isArray(employeeWithAllocations.activityAllocations)
                  ) {
                    employeeWithAllocations.activityAllocations.forEach((aa) => {
                      activityAllocationsRecord[aa.activityId] = aa.allocation;
                    });
                  }

                  updated[employeeWithAllocations.id] = {
                    ...updated[employeeWithAllocations.id],
                    projectAllocation: projectAllocation.allocation,
                    roleDescription: projectAllocation.roleDescription || '',
                    startDate: new Date(projectAllocation.startDate),
                    endDate: projectAllocation.endDate
                      ? new Date(projectAllocation.endDate)
                      : undefined,
                    activityAllocations: activityAllocationsRecord,
                    hasExistingAllocation: true,
                  };
                }
              } else {
                console.warn('Employee data structure missing expected fields:', {
                  employeeId: employeeWithAllocations.id,
                  hasProjectAllocations: !!employeeWithAllocations.projectAllocations,
                  projectAllocationsType: typeof employeeWithAllocations.projectAllocations,
                  isArray: Array.isArray(employeeWithAllocations.projectAllocations),
                });
              }
            });

            return updated;
          });
        } else {
          console.warn('Invalid response structure:', {
            success: data.success,
            hasEmployees: !!data.employees,
            employeesType: typeof data.employees,
            isArray: Array.isArray(data.employees),
          });
        }
      } else {
        console.error('Failed to fetch project employees:', response.status, response.statusText);
      }
    } catch (err) {
      console.error('Failed to load existing allocations:', err);
      enqueueSnackbar('Failed to load existing employee allocations', { variant: 'warning' });
    }
  };

  // Memoized event handlers for row components
  const handleProjectAllocationChange = useCallback((employeeId: string, value: string) => {
    const percentage = parseFloat(value) || 0;
    if (percentage >= 0 && percentage <= 100) {
      setAllocations((prev) => ({
        ...prev,
        [employeeId]: {
          ...prev[employeeId],
          projectAllocation: percentage,
          // Reset activity allocations if project allocation becomes 0
          activityAllocations: percentage === 0 ? {} : prev[employeeId].activityAllocations,
        },
      }));
    }
  }, []);

  const handleRoleDescriptionChange = useCallback((employeeId: string, value: string) => {
    setAllocations((prev) => ({
      ...prev,
      [employeeId]: {
        ...prev[employeeId],
        roleDescription: value,
      },
    }));
  }, []);

  const handleActivityAllocationChange = useCallback(
    (employeeId: string, activityId: string, value: string) => {
      const percentage = parseFloat(value) || 0;
      if (percentage >= 0 && percentage <= 100) {
        setAllocations((prev) => ({
          ...prev,
          [employeeId]: {
            ...prev[employeeId],
            activityAllocations: {
              ...prev[employeeId].activityAllocations,
              [activityId]: percentage,
            },
          },
        }));
      }
    },
    []
  );

  const handleRemoveAllocation = useCallback(
    async (employeeId: string) => {
      if (!organisationId || !projectId) return;

      try {
        setSaving(employeeId);

        const response = await apiClient.delete(
          `/organisations/${organisationId}/projects/${projectId}/employees/${employeeId}`
        );

        if (response.ok) {
          const data: EmployeeAllocationDeleteResponse = await response.json();

          if (data.success) {
            // Reset the allocation for this employee
            setAllocations((prev) => ({
              ...prev,
              [employeeId]: {
                ...prev[employeeId],
                projectAllocation: 0,
                roleDescription: '',
                startDate: new Date(),
                endDate: undefined,
                activityAllocations: {},
              },
            }));

            enqueueSnackbar('Employee allocation removed successfully', { variant: 'success' });
          } else {
            enqueueSnackbar(data.message || 'Failed to remove allocation', { variant: 'error' });
          }
        } else {
          const errorData = await response.json();
          enqueueSnackbar(errorData.message || 'Failed to remove allocation', { variant: 'error' });
        }
      } catch (err) {
        enqueueSnackbar(err instanceof Error ? err.message : 'Failed to remove allocation', {
          variant: 'error',
        });
      } finally {
        setSaving(null);
      }
    },
    [organisationId, projectId, enqueueSnackbar]
  );

  const handleSaveAllocation = useCallback(
    async (employeeId: string) => {
      if (!organisationId || !projectId) return;

      try {
        setSaving(employeeId);

        const allocation = allocations[employeeId];

        // Validate required fields
        if (!allocation.roleDescription.trim()) {
          enqueueSnackbar('Role description is required', { variant: 'error' });
          return;
        }

        if (allocation.projectAllocation <= 0) {
          enqueueSnackbar('Project allocation must be greater than 0%', { variant: 'error' });
          return;
        }

        const requestBody: EmployeeAllocationUpdateRequestBody = {
          allocation: allocation.projectAllocation,
          roleDescription: allocation.roleDescription,
          startDate: allocation.startDate,
          endDate: allocation.endDate,
          activityAllocations: allocation.activityAllocations,
        };

        const url = `/organisations/${organisationId}/projects/${projectId}/employees/${employeeId}`;
        const response = allocation.hasExistingAllocation
          ? await apiClient.put(url, requestBody) // Update existing
          : await apiClient.post(url, requestBody); // Create new

        if (response.ok) {
          const data: EmployeeAllocationUpdateResponse = await response.json();

          if (data.success) {
            enqueueSnackbar('Employee allocation saved successfully', { variant: 'success' });

            // Find the project allocation that was just created/updated
            const projectAllocation = data.projectAllocations.find(
              (pa) => pa.projectId === projectId
            );

            if (projectAllocation) {
              // Convert activity allocations array to Record format
              const activityAllocationsRecord: Record<string, number> = {};
              data.activityAllocations.forEach((aa) => {
                activityAllocationsRecord[aa.activityId] = aa.allocation;
              });

              setAllocations((prev) => ({
                ...prev,
                [employeeId]: {
                  ...prev[employeeId],
                  projectAllocation: projectAllocation.allocation,
                  roleDescription: projectAllocation.roleDescription,
                  startDate: new Date(projectAllocation.startDate),
                  endDate: projectAllocation.endDate
                    ? new Date(projectAllocation.endDate)
                    : undefined,
                  activityAllocations: activityAllocationsRecord,
                },
              }));
            }
          } else {
            enqueueSnackbar(data.message || 'Failed to save allocation', { variant: 'error' });
          }
        } else {
          const errorData = await response.json();
          enqueueSnackbar(errorData.message || 'Failed to save allocation', { variant: 'error' });
        }
      } catch (err) {
        enqueueSnackbar(err instanceof Error ? err.message : 'Failed to save allocation', {
          variant: 'error',
        });
      } finally {
        setSaving(null);
      }
    },
    [organisationId, projectId, allocations, enqueueSnackbar]
  );

  const sortedActivities = useMemo(() => {
    return [...activities].sort((a, b) => {
      return a.sortOrder - b.sortOrder;
    });
  }, [activities]);

  if (loading) {
    return (
      <ContentWrapper>
        <PaperSection title="R&D Activities">
          <LoadingComponent context="Loading employees..." />
        </PaperSection>
      </ContentWrapper>
    );
  }

  if (!organisationId || !projectId) {
    return (
      <ContentWrapper>
        <EmptyStateContent
          icon={<PeopleOutlined />}
          title="No project selected"
          message="Please select a project to allocate employee activities."
        />
      </ContentWrapper>
    );
  }

  return (
    <ContentWrapper>
      <PaperSection
        title={`Employee Activity Allocation - ${currentProject?.name}`}
        action={
          <>
            <XeroSyncEmployees onSyncComplete={handleSyncComplete} />
            <HelpComponent title="Assign employee time to research activities.">
              <Typography variant="body2" paragraph>
                Allocate employees to the project and distribute their time across research
                activities. Employees must be allocated to at least one activity to be included in
                R&D claims. Research role is required for audit purposes.
              </Typography>

              <Divider sx={{ my: 1 }} />

              <Stack spacing={0.5}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonAddAlt1 sx={{ fontSize: '16px', color: 'success.main' }} />
                  <Typography variant="caption">Creates a new allocation</Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonTickIcon sx={{ fontSize: '16px', color: 'success.main' }} />
                  <Typography variant="caption">Saves an employee allocation</Typography>
                </Stack>

                <Stack direction="row" spacing={1} alignItems="center">
                  <PersonRemoveAlt1Outlined sx={{ fontSize: '16px', color: 'error.main' }} />
                  <Typography variant="caption">Removes allocation</Typography>
                </Stack>
              </Stack>
            </HelpComponent>
          </>
        }
      >
        {/* Filters */}
        <Box
          sx={{
            mb: 1,
            mt: -2,
            display: 'flex',
            gap: 2,
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          {employees.length > 0 && (
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              <FormControlLabel
                control={
                  <Switch checked={activeOnly} onChange={(e) => setActiveOnly(e.target.checked)} />
                }
                label={
                  <Typography variant="body2">
                    {financialYearRange
                      ? `Employed during FY ${currentProject?.taxYear}`
                      : 'Active only'}
                  </Typography>
                }
              />

              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Employee Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Employee Type"
                  onChange={(e) => setTypeFilter(e.target.value as EmployeeType | '')}
                >
                  <MenuItem value="">All Types</MenuItem>
                  <MenuItem value={EmployeeType.EMPLOYEE}>Employee</MenuItem>
                  <MenuItem value={EmployeeType.CONTRACTOR}>Contractor</MenuItem>
                </Select>
              </FormControl>
            </Box>
          )}

          {syncResult?.success && (
            <Stack direction="row" alignItems="center" spacing={2}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <CheckCircle color="success" sx={{ fontSize: 16 }} />
                <Typography variant="body2" color="success.main" fontWeight={500}>
                  Employees synced successfully
                </Typography>
              </Stack>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Typography variant="body2" color="text.secondary">
                  {syncResult.processed} processed
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  •
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {syncResult.created} created
                </Typography>
                {syncResult.updated > 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      •
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {syncResult.updated} updated
                    </Typography>
                  </>
                )}
                {syncResult.skipped > 0 && (
                  <>
                    <Typography variant="body2" color="text.secondary">
                      •
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {syncResult.skipped} skipped
                    </Typography>
                  </>
                )}
              </Box>
            </Stack>
          )}
        </Box>

        {employees.length === 0 ? (
          <Box sx={{ my: 10 }}>
            <EmptyStateContent
              icon={<PeopleOutlined />}
              title="No employees found"
              message="No employees match your current filters. Employees are automatically synced from Xero."
              showBorder={false}
            />
          </Box>
        ) : activities.length === 0 ? (
          <Box sx={{ my: 10 }}>
            <EmptyStateContent
              icon={<ScienceOutlined />}
              title="No research activities"
              message="Create research activities first to start allocating employees."
              button={
                <Button
                  variant="contained"
                  onClick={() => navigate(`/app/activities/${projectId}`)}
                >
                  Create your first activity
                </Button>
              }
              showBorder={false}
            />
          </Box>
        ) : (
          <TableContainer
            sx={{
              mx: -6,
              px: 6,
              width: 'calc(100% + 96px)',
              // maxHeight: '70vh', // this forces vertical scroll in the panel
              // minHeight: '60vh',
              //  height: '70vh',
              position: 'relative',
              overflowX: 'scroll',
              '&::-webkit-scrollbar': {
                WebkitAppearance: 'none',
                width: '8px',
              },
              '&::-webkit-scrollbar-thumb': {
                borderRadius: '4px',
                backgroundColor: 'rgba(0, 0, 0, .3)',
              },
            }}
          >
            <Table stickyHeader size="small">
              <TableHead
                sx={{
                  '&::-webkit-scrollbar-thumb': {
                    borderRadius: '4px',
                    backgroundColor: 'rgba(0, 0, 0, .3)',
                  },
                  '& .MuiTableCell-head': {
                    zIndex: (theme) => theme.zIndex.appBar + 1,
                    backgroundColor: 'background.paper',
                    borderBottom: '2px solid',
                    borderColor: 'divider',
                    borderTop: '0px none',
                  },
                }}
              >
                <TableRow>
                  <TableCell sx={{ width: 350, minWidth: 350, fontWeight: 600 }}>
                    Employee Details
                  </TableCell>
                  <TableCell sx={{ width: 40, minWidth: 40, textAlign: 'center', fontWeight: 600 }}>
                    Actions
                  </TableCell>
                  <TableCell
                    sx={{
                      width: 120,
                      minWidth: 120,
                      fontWeight: 600,
                      backgroundColor: '#EFF2FE !important',
                      textAlign: 'center',
                    }}
                  >
                    Project Allocation %
                  </TableCell>
                  <TableCell sx={{ width: 50, minWidth: 50, textAlign: 'center', fontWeight: 600 }}>
                    Total
                  </TableCell>
                  {sortedActivities.map((activity) => (
                    <TableCell
                      key={activity.id}
                      sx={{ width: 100, minWidth: 100, p: 1, verticalAlign: 'top' }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Chip
                          label={activity.type === ResearchActivityType.CORE ? 'CORE' : 'SUPP'}
                          size="small"
                          color={activity.type === ResearchActivityType.CORE ? 'info' : 'primary'}
                          sx={{ height: 18, fontSize: '0.65rem', '& .MuiChip-label': { px: 0.75 } }}
                        />
                        <Typography
                          variant="caption"
                          fontWeight={600}
                          align="center"
                          sx={{ lineHeight: 1.2 }}
                        >
                          {activity.name}
                        </Typography>
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {employees.map((employee) => (
                  <EmployeeAllocationRow
                    key={employee.id}
                    employee={employee}
                    allocation={allocations[employee.id]}
                    activities={sortedActivities}
                    saving={saving === employee.id}
                    onProjectAllocationChange={handleProjectAllocationChange}
                    onRoleDescriptionChange={handleRoleDescriptionChange}
                    onActivityAllocationChange={handleActivityAllocationChange}
                    onSaveAllocation={handleSaveAllocation}
                    onRemoveAllocation={handleRemoveAllocation}
                  />
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        {/* Summary */}
        {activities.length > 0 && employees.length > 0 && (
          <Box
            sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
          >
            <Typography variant="body2" color="text.secondary">
              Showing {employees.length} employee{employees.length !== 1 ? 's' : ''}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Activity allocations must total 100%
            </Typography>
          </Box>
        )}
      </PaperSection>
    </ContentWrapper>
  );
};

export default Employees;
